/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React from 'react';
import ImageAndTimeline from 'components/detail/common/ImageAndTimeline';
import { ISurfaceIdentificationProps } from 'components/detail/surface/SurfaceIdentification';
import { Identification, DetailAccordionCard, DetailName, labels } from '@umati/dashboard-common';
import { DInfoRects } from 'components/_lib/Details';
import { Accordion } from 'react-bootstrap';
import { connect } from 'react-redux';
import { IMachine, IPositionFair, IFair } from '@umati/dashboard-types';
import { getFair, getMachine, getMachinesByParent } from 'redux/selectors';
import MachineListElement from 'components/machineList/machineListElement';
import SurfaceMeta, { ISurfaceMetaProps } from 'components/detail/surface/SurfaceMeta';
import SurfaceProcess, { ISurfaceProcess } from 'components/detail/surface/SurfaceProcess';
import SurfaceState, { ISurfaceStateProps } from 'components/detail/surface/SurfaceState';
import MachineHandling from 'utilities/MachineHandling';
import Header from 'components/header/header';
import FrontendURLs from 'utilities/FrontendURLs';
import { SurfaceParameterTypeList } from 'components/detail/surface/SurfaceTypes';
import SurfaceParameterTypes from 'components/detail/surface/SurfaceParameterTypes';

interface IRoutingParameters {
  machineId: string;
  typeId: string
  fairTag?: string;
}

interface ISurfaceTechnologyMachine {
  '<Components>'?: {
    'Components'?: {
      '<Component>'?: SurfaceParameterTypeList
    };
  };
  Identification?: ISurfaceIdentificationProps;
  Meta?: ISurfaceMetaProps;
  Process?: ISurfaceProcess;
  State?: ISurfaceStateProps;
}

interface ISurfaceTechnologyDetailBase extends IRoutingParameters {
  data?: ISurfaceTechnologyMachine;
}

interface ISurfaceTechnologyDetailStore {
  machine?: IMachine;
  subMachines: IMachine[];
  fair?: IFair;
}

interface ISurfaceTechnologyDetailProps extends ISurfaceTechnologyDetailBase, ISurfaceTechnologyDetailStore {

}

function SurfaceTechnologyDetail (props: ISurfaceTechnologyDetailProps) {
  const machine = props.data;

  const identification = machine?.Identification || props.machine?.Data || {};
  const productInstanceUri = machine?.Identification?.ProductInstanceUri || props.machine?.Data.ProductInstanceUri;
  const position = props.fairTag ? props.machine?.Positions?.[props.fairTag] as IPositionFair : null;
  const detailRectProps = [
    { label: labels.hall, value: position?.hall },
    { label: labels.booth, value: position?.booth }];
  const subMachines: IMachine[] = props.subMachines;
  const machineName = MachineHandling.getMachineName(props.machine);
  const anchor = machineName?.replace(/\s+/g, '') || '';
  const headerDetail = {
    text: machineName,
    link: FrontendURLs.getMachine({ TypeDefinition: props.typeId, MachineId: props.machineId }, props.fairTag),
    anchor
  };

  const extraComponents = machine?.['<Components>']?.Components?.['<Component>'];
  return (
    <div>
      <Header {...props}
        fairTag={props.fairTag || 'all'}
        detail={headerDetail}
        listType="machines"
        containerClass="container-max" />
      <div className="view-machine-detail">
        <div className="hero">
          <div className="container-max">
            <DetailName isOnline={props?.machine?.Online} name={productInstanceUri}
              model={machineName} />
            {position && <DInfoRects infos={detailRectProps} />}
          </div>
        </div>
        <div className="container-max">
          <ImageAndTimeline imagePath={props.machine?.ImagePath!} hideTimeline={true} machineId={props.machineId} />
          <div className="row justify-content-end">
            <div className="col-12 offset-lg-2 col-lg-9">
              <Accordion defaultActiveKey="0" className="mb-4">
                <DetailAccordionCard eventKey="0" title="Identification" isExpanded={true}>
                  <Identification {...identification} />
                </DetailAccordionCard>
                {machine?.Meta !== undefined && <DetailAccordionCard eventKey="1" title="Meta" isExpanded={false}>
                  <SurfaceMeta {...machine.Meta} />
                </DetailAccordionCard>}
                {machine?.Process !== undefined && <DetailAccordionCard eventKey="2" title="Process" isExpanded={false}>
                  <SurfaceProcess {...machine.Process} />
                </DetailAccordionCard>}
                {machine?.State !== undefined && <DetailAccordionCard eventKey="3" title="State" isExpanded={false}>
                  <SurfaceState {...machine.State} />
                </DetailAccordionCard>}
                {extraComponents !== undefined && <DetailAccordionCard eventKey="4" title="Component Process" isExpanded={false}>
                  <div className="section surface-process">
                    <div className="row">
                      <SurfaceParameterTypes list={extraComponents} />
                    </div>
                  </div>
                </DetailAccordionCard>
                }
              </Accordion>
              <div className="view-list">
                <ul className="list-group">
                  {subMachines.map(m =>
                    <MachineListElement
                      hideFav={true}
                      hideOnline={true}
                      key={m.MachineId}
                      machine={m}
                      fairTag={props.fairTag || 'all'} />
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}

const mapStateToProps = (state, ownProps: ISurfaceTechnologyDetailBase): ISurfaceTechnologyDetailStore => {
  return {
    fair: getFair(state, ownProps.fairTag),
    machine: getMachine(state, ownProps.machineId),
    subMachines: getMachinesByParent(state, ownProps.machineId)
  };
};

const SurfaceTechnology = connect(mapStateToProps)(SurfaceTechnologyDetail);
export default SurfaceTechnology;
